import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { DedicatedEngine } from '@rossum/api-client/dedicatedEngines';
import { GenericEngine } from '@rossum/api-client/genericEngines';
import { shouldDisplayAuroraChipByName } from '@rossum/components/aurora-chip/AuroraChip';
import { Tile, TileQuickAction } from '@rossum/components/tile';
import { IconCustomAurora } from '@rossum/ui/icons/tabler';
import { poweredByAuroraSelector } from '../../../redux/modules/organization/selectors';
import { auroraEngineEnabledSelector } from '../../../redux/modules/organizationGroup/selectors';
import { isDedicatedEngine } from '../../legacy-engines/hooks/utils';
import { useQueuesForEngine } from '../hooks/useQueuesForEngine';

const VISIBLE_CHIPS_COUNT = 3;

const statusColorMap = {
  sample_review: 'warning',
  draft: 'info',
  schema_review: 'warning',
  annotating_initial: 'info',
  annotating_review: 'warning',
  annotating_training: 'info',
  training_started: 'info',
  training_finished: 'success',
  retraining: 'info',
  active: 'success',
} as const;

type EngineCardProps = {
  engine: DedicatedEngine | GenericEngine;
  quickActions?: TileQuickAction[];
};

const EngineTile = ({ engine, quickActions }: EngineCardProps) => {
  const intl = useIntl();

  const auroraEngineEnabled = useSelector(auroraEngineEnabledSelector);
  const auroraPromoVisible = useSelector(poweredByAuroraSelector);

  const shouldDisplayAuroraChip =
    shouldDisplayAuroraChipByName(engine.name, auroraEngineEnabled) &&
    auroraPromoVisible;

  const isDedicated = isDedicatedEngine(engine);

  const queuesQuery = isDedicated
    ? { dedicatedEngine: [engine.id] }
    : { genericEngine: [engine.id] };

  const { data, isFetching } = useQueuesForEngine(queuesQuery);

  const queuesInfo = useMemo(
    () => ({
      info: data?.info ?? [],
      totalCount: data?.pagination?.total ?? 0,
    }),
    [data]
  );

  const inlineBadges = useMemo(() => {
    if (isDedicated) {
      const status =
        queuesInfo.totalCount > 0 && engine.status === 'training_finished'
          ? 'active'
          : engine.status;

      return [
        {
          key: 'status',
          label: intl.formatMessage({
            id: `components.engineStatusChip.${status}`,
          }),
          color: statusColorMap[status],
        },
      ];
    }

    const statusBadge =
      queuesInfo.totalCount > 0
        ? [
            {
              key: 'status',
              label: intl.formatMessage({
                id: 'components.engineStatusChip.active',
              }),
              color: 'success' as const,
            },
          ]
        : [];

    const auroraBadge = shouldDisplayAuroraChip
      ? [
          {
            key: 'aurora',
            label: intl.formatMessage({ id: 'component.auroraChip.label' }),
            Icon: IconCustomAurora,
            color: 'aurora' as const,
          },
        ]
      : [];

    return [...statusBadge, ...auroraBadge];
  }, [
    isDedicated,
    queuesInfo.totalCount,
    engine,
    shouldDisplayAuroraChip,
    intl,
  ]);

  const queueChips = useMemo(() => {
    if (isFetching) {
      return [
        { key: 'skeleton-1', label: '', isLoading: true },
        { key: 'skeleton-2', label: '', isLoading: true },
      ];
    }

    if (queuesInfo.info.length === 0) {
      return [
        {
          key: 'empty',
          label: intl.formatMessage({
            id: 'features.engines.engineDetail.noQueue',
          }),
          color: 'transparent' as const,
        },
      ];
    }

    const remainingCount = queuesInfo.totalCount - VISIBLE_CHIPS_COUNT;

    const visibleChips = queuesInfo.info
      .slice(0, VISIBLE_CHIPS_COUNT)
      .map(info => ({
        key: String(info.id),
        label: `${info.workspace} - ${info.queue}`,
        tooltip: `${info.workspace} - ${info.queue}`,
      }));

    return [
      ...visibleChips,
      ...(remainingCount > 0
        ? [
            {
              key: 'more',
              label: `+${remainingCount}`,
              tooltip: queuesInfo.info
                .slice(VISIBLE_CHIPS_COUNT)
                .map(info => `${info.workspace} - ${info.queue}`)
                .join(', '),
            },
          ]
        : []),
    ];
  }, [queuesInfo, isFetching, intl]);

  return (
    <Tile
      title={engine.name}
      description={engine.description}
      inlineBadges={inlineBadges.length > 0 ? inlineBadges : undefined}
      chips={queueChips}
      quickActions={quickActions}
    />
  );
};

export { EngineTile };
